import {
  createAsyncThunk,
  createSelector,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';
import {
  CreateSalesfloorRoomRequest,
  SalesfloorConversation,
  SalesfloorRoom,
  SalesfloorRoomParticipantStatus,
  SalesfloorRoomParticipant,
  SalesfloorUserConversations,
  SalesfloorRoomChatParticipant,
  SalesfloorRoomChatParticipantStatus,
} from 'types/salesfloor';
import {
  createSalesfloorRoom,
  deleteSalesfloorRoom,
  getSalesfloorRooms,
} from 'shared/api';
import { sendSocketSalesfloorChatMessage } from 'shared/realtime';
import {
  SalesfloorChatMessage,
  SalesfloorChannelUpdate,
  SalesfloorParticipant,
  OwnUser,
  SalesfloorChatTypingNotificationMessage,
  SalesfloorChatMessageType,
  SalesfloorChatMessageArgs,
} from 'sb-lib/types';
import type { AppState } from 'shared/store/reducers';
import { RejectError } from 'shared/api/axios';
import { SalesfloorReactionType } from 'browser/components/SalesfloorShared/SalesfloorRoomToolbar/Reactions';
import { selectActivePublicUser, selectActiveUser } from './shared-selectors';
import { makeLog } from 'shared/utils/log';
import { uploadSignedPostWithProgress } from 'shared/utils/s3';
import {
  chatImageUploadErrorChanged,
  chatImageUploadProgressChanged,
} from './chat-slice';
import { UserAccounType } from 'types/user';

const log = makeLog('salesfloor-state-slice');

export const initialState = {
  chatOpen: false,
  chatExplicitlyClosed: false,
  mode: 'room' as 'room' | 'dialer',
  screenShareInFocus: null as null | string,
  screenSharing: false,
  camera: false,
  muted: false,
  settingsModalVisible: false,
  settingsModalVariant: null as null | 'join' | 'settings' | 'public-signin',
  reaction: null as null | SalesfloorReactionType,
  sidebarCollapsed: false,
  privateRooms: [] as SalesfloorRoom[],
  teamRooms: [] as SalesfloorRoom[],
  publicRooms: [] as SalesfloorRoom[],
  liveListenRooms: [] as SalesfloorRoom[],
  localParticipantStatus: null as SalesfloorRoomParticipantStatus | null,
  listenedDialingParticipantIds: {} as Record<string, boolean>,
  activeConversationId: '',
  conversations: {} as SalesfloorUserConversations,
  chatParticipants: [] as SalesfloorRoomChatParticipant[],
  roomsLoading: true,
};

export const createRoom = createAsyncThunk<
  SalesfloorRoom,
  CreateSalesfloorRoomRequest,
  { state: AppState; rejectValue: RejectError }
>('salesfloor/createRoom', async (room, { dispatch }) => {
  const res = await createSalesfloorRoom({
    name: room.name,
    privacyMode: room.privacyMode,
    emoji: room.emoji,
    cover: room.cover,
  });
  dispatch(salesfloorSlice.actions.addRoom(res.data));
  return res.data;
});

export const deleteRoom = createAsyncThunk<
  void,
  SalesfloorRoom,
  { state: AppState; rejectValue: RejectError }
>('salesfloor/deleteRoom', async (room, { dispatch }) => {
  await deleteSalesfloorRoom(room.id);
  dispatch(salesfloorSlice.actions.removeSalesfloorRoom({ roomId: room.id }));
});

export const fetchAllRooms = createAsyncThunk<
  void,
  void,
  { state: AppState; rejectValue: RejectError }
>('salesfloor/fetchAllRooms', async (_, { dispatch }) => {
  const rooms = await Promise.all([
    getSalesfloorRooms('private', 1000),
    getSalesfloorRooms('team', 1000),
    getSalesfloorRooms('public', 1000),
    getSalesfloorRooms('live_listen', 1000),
  ]);

  dispatch(salesfloorSlice.actions.setSalesfloorRooms(rooms.map(r => r.data).flat()));
  dispatch(salesfloorSlice.actions.setRoomsLoading(false));
});

export const addConversationMessage = createAsyncThunk<
  void,
  { message: SalesfloorChatMessage; messageArgs: SalesfloorChatMessageArgs },
  { state: AppState; rejectValue: RejectError }
>(
  'salesfloor/addConversationMessage',
  async ({ message, messageArgs }, { dispatch, getState }) => {
    const state = getState();
    const activeUser = selectActiveUser(state);
    const publicUser = selectActivePublicUser(state);
    const ownUser = activeUser ? activeUser : publicUser;
    if (!ownUser) {
      log.warn('Could not resolve current user or public user');
      return;
    }
    const { registeredImg, file } = messageArgs;
    const update = {
      message,
      ownUser: {
        id: ownUser.id,
        type: activeUser ? UserAccounType.REGULAR : UserAccounType.PUBLIC,
      },
    };
    dispatch(salesfloorSlice.actions.addConversationChatMessage(update));

    if (registeredImg && file && message.conversationId) {
      try {
        const uploadRes = await uploadSignedPostWithProgress(
          file,
          registeredImg.uploadUrl,
          (percent: number) => {
            dispatch(chatImageUploadProgressChanged(percent));
          },
        );
        if (uploadRes.status !== 204) {
          const error = `Failed to upload image (${uploadRes.status})`;
          dispatch(chatImageUploadErrorChanged(error));
          throw new Error(error);
        }
      } catch (e) {
        let message = 'unknown reason';
        if ('message' in e) {
          message = (e as { message: string }).message;
        }
        dispatch(chatImageUploadErrorChanged(`Image upload failed: ${message}`));
        throw new Error(`Image upload failed: ${message}`);
      }
    }

    try {
      await sendSocketSalesfloorChatMessage(message);
    } catch (error) {
      dispatch(salesfloorSlice.actions.removeConversationChatMessage(update));
      log.error(`Error occurred while sending a Salesfloor Chat message: ${error}`);
    }
  },
);

function emptyConversation(id: string): SalesfloorConversation {
  return {
    id,
    participants: [],
    chatMessages: [],
    isTypingState: {},
    unreadMessages: 0,
  };
}

function getConversation(
  state: { conversations: SalesfloorUserConversations },
  conversationId: string,
): SalesfloorConversation {
  if (!state.conversations[conversationId]) {
    state.conversations[conversationId] = emptyConversation(conversationId);
  }
  return state.conversations[conversationId];
}

function appendConversationMessage(
  state: {
    activeConversationId: string;
    conversations: SalesfloorUserConversations;
    chatOpen: boolean;
    chatExplicitlyClosed: boolean;
  },
  message: SalesfloorChatMessage,
) {
  const conversationId = message.conversationId;
  const conversation = getConversation(state, conversationId);

  const oldMessages = new Set(conversation.chatMessages.map(m => m.id));
  if (oldMessages.has(message.id)) {
    return; // drop duplicate messages
  }

  if (message.type == SalesfloorChatMessageType.CONTENT) {
    if (conversationId == state.activeConversationId && state.chatOpen) {
      conversation.unreadMessages = 0;
    } else if (
      conversationId !== state.activeConversationId &&
      !state.chatOpen &&
      !state.chatExplicitlyClosed
    ) {
      state.activeConversationId = conversationId;
      state.chatOpen = true;
      conversation.unreadMessages = 0;
    } else if (conversationId !== state.activeConversationId && state.chatOpen) {
      state.chatOpen = true;
      conversation.unreadMessages++;
    } else if (!state.chatExplicitlyClosed) {
      state.chatOpen = true;
    } else if (state.chatExplicitlyClosed) {
      conversation.unreadMessages++;
    }
  }

  conversation.chatMessages = [message, ...conversation.chatMessages];
  updateConversationMessages(conversation);
}

function removeConversationMessage(
  state: {
    activeConversationId: string;
    conversations: SalesfloorUserConversations;
    chatOpen: boolean;
    chatExplicitlyClosed: boolean;
  },
  message: SalesfloorChatMessage,
) {
  if (message.type !== SalesfloorChatMessageType.CONTENT) {
    return;
  }

  const conversationId = message.conversationId;
  const conversation = getConversation(state, conversationId);
  conversation.chatMessages = conversation.chatMessages.filter(
    chatMessage => chatMessage.id !== message.id,
  );
  updateConversationMessages(conversation);
}

function updateIsTypingState(
  state: { activeConversationId: string; conversations: SalesfloorUserConversations },
  ownUser: OwnUser,
  message: SalesfloorChatTypingNotificationMessage,
) {
  const { conversationId, sender: participant } = message;
  const ownUserId =
    ownUser.type === UserAccounType.PUBLIC
      ? `public_user_${ownUser.id}`
      : `user_${ownUser.id}`;
  if (ownUserId == message.sender.id) {
    log.debug('ignoring own typing update message', message);
    return;
  }
  if (conversationId != state.activeConversationId) {
    log.debug('ignoring typing update message for inactive conversation', message);
    return;
  }

  setTypingState(state, { conversationId, participant, isTyping: message.isTyping });
}

function setTypingState(
  state: { conversations: SalesfloorUserConversations },
  update: {
    conversationId: string;
    participant: SalesfloorParticipant;
    isTyping: boolean;
  },
) {
  const { conversationId, participant, isTyping } = update;
  const conversation = getConversation(state, conversationId);
  const previousState = conversation.isTypingState[participant.id];
  const newState = { participant, isTyping, timestamp: Date.now() };
  if (previousState && previousState.isTyping == newState.isTyping) return;

  conversation.isTypingState = {
    ...conversation.isTypingState,
    [participant.id]: newState,
  };
  updateConversationMessages(conversation);
}

function updateConversationMessages(conversation: SalesfloorConversation) {
  const isContentMessage = (m: SalesfloorChatMessage): boolean =>
    m.type == SalesfloorChatMessageType.CONTENT;

  // drop typing notification messages
  const messages = conversation.chatMessages
    .filter(isContentMessage)
    .sort((m1, m2) => m2.timestamp - m1.timestamp);
  // add new typing notification message for every participant that is currently typing
  Object.values(conversation.isTypingState).forEach(typingState => {
    if (!typingState.isTyping) return;

    messages.unshift({
      type: SalesfloorChatMessageType.TYPING,
      id: `${conversation.id}-${typingState.participant.id}-${typingState.timestamp}`,
      timestamp: typingState.timestamp,
      isTyping: true,
      conversationId: conversation.id,
      sender: typingState.participant,
    });
  });

  conversation.chatMessages = messages;
}

export const sendChatTyping = createAsyncThunk<
  void,
  { participant: SalesfloorParticipant; isTyping: boolean; conversationId: string },
  { state: AppState }
>('salesfloor/sendChatTyping', ({ participant, isTyping, conversationId }, _) => {
  log.debug('sendChatTyping', isTyping);
  const timestamp = Date.now();
  const message: SalesfloorChatTypingNotificationMessage = {
    id: `${conversationId}-${participant.id}-${timestamp}`,
    timestamp: timestamp,
    type: SalesfloorChatMessageType.TYPING,
    isTyping: isTyping,
    conversationId: conversationId,
    sender: {
      ...participant,
      id: `${participant.id}`,
    },
  };
  sendSocketSalesfloorChatMessage(message);
});

const salesfloorSlice = createSlice({
  name: 'salesfloor',
  initialState,
  reducers: {
    setRoomsLoading: (state, action: PayloadAction<boolean>) => {
      state.roomsLoading = action.payload;
    },
    setChatOpen(state, action: PayloadAction<boolean>) {
      state.chatOpen = action.payload;

      if (!action.payload) {
        state.chatExplicitlyClosed = true;
      }

      const activeConversation = getConversation(state, state.activeConversationId);

      if (activeConversation.unreadMessages > 0) {
        activeConversation.unreadMessages = 0;
      }
    },
    setMode(state, action: PayloadAction<'room' | 'dialer'>) {
      state.mode = action.payload;
    },
    setScreenShareInFocus(state, action: PayloadAction<string | null>) {
      state.screenShareInFocus = action.payload;
    },
    unsetScreenShareInFocus(state, action: PayloadAction<string | null>) {
      if (state.screenShareInFocus === action.payload) {
        state.screenShareInFocus = null;
      }
    },
    setScreenSharing(state, action: PayloadAction<boolean>) {
      state.screenSharing = action.payload;
    },
    addRoom(state, action: PayloadAction<SalesfloorRoom>) {
      if (action.payload.privacyMode === 'private') {
        state.privateRooms.unshift(action.payload);
      } else if (action.payload.privacyMode === 'team') {
        state.teamRooms.unshift(action.payload);
      } else {
        state.publicRooms.unshift(action.payload);
      }
    },
    setSalesfloorRooms(state, action: PayloadAction<SalesfloorRoom[]>) {
      state.privateRooms = [];
      state.teamRooms = [];
      state.publicRooms = [];
      state.liveListenRooms = [];

      action.payload.forEach(room => {
        if (room.privacyMode === 'private') {
          state.privateRooms.push(room);
        } else if (room.privacyMode === 'team') {
          state.teamRooms.push(room);
        } else if (room.privacyMode === 'live_listen') {
          state.liveListenRooms.push(room);
        } else {
          state.publicRooms.push(room);
        }
      });
    },
    removeSalesfloorRoom(state, action: PayloadAction<{ roomId: string }>) {
      state.privateRooms = state.privateRooms.filter(
        room => room.id !== action.payload.roomId,
      );
      state.teamRooms = state.teamRooms.filter(room => room.id !== action.payload.roomId);
      state.publicRooms = state.publicRooms.filter(
        room => room.id !== action.payload.roomId,
      );
    },
    setSettingsModalVisible(
      state,
      action: PayloadAction<{
        visible: boolean;
        variant?: 'join' | 'settings' | 'public-signin';
      }>,
    ) {
      state.settingsModalVisible = action.payload.visible;
      if (!action.payload.variant) return;
      state.settingsModalVariant = action.payload.variant;
    },
    setReaction(state, action: PayloadAction<null | SalesfloorReactionType>) {
      state.reaction = action.payload;
    },
    setSidebardCollapsed(state, action: PayloadAction<boolean>) {
      state.sidebarCollapsed = action.payload;
    },
    updateSalesfloorRoom(state, action: PayloadAction<SalesfloorRoom>) {
      const newRoom = action.payload;
      const rooms = [...state.privateRooms, ...state.teamRooms, ...state.publicRooms];
      const index = rooms.findIndex(r => r.id === newRoom.id);
      if (index === -1) return;

      const existingRoom = rooms[index];
      if (existingRoom.version >= newRoom.version) return;

      rooms[index] = newRoom;
      state.privateRooms = rooms.filter(r => r.privacyMode === 'private');
      state.teamRooms = rooms.filter(r => r.privacyMode === 'team');
      state.publicRooms = rooms.filter(r => r.privacyMode === 'public');

      const connectedParticipantIds = rooms
        .flatMap(r => r.participants.filter(p => p.status === 'connected'))
        .map(p => p.id);

      const listenedDialingParticipantIds = { ...state.listenedDialingParticipantIds };
      Object.keys(listenedDialingParticipantIds).forEach(id => {
        if (!connectedParticipantIds.includes(id)) {
          listenedDialingParticipantIds[id] = false;
        }
      });

      state.listenedDialingParticipantIds = listenedDialingParticipantIds;
    },
    setLocalParticipantStatus(
      state,
      action: PayloadAction<SalesfloorRoomParticipantStatus | null>,
    ) {
      state.localParticipantStatus = action.payload;
    },
    setListenedDialingParticipantIds(
      state,
      action: PayloadAction<{ id: string; listens: boolean }>,
    ) {
      state.listenedDialingParticipantIds[action.payload.id] = action.payload.listens;
    },
    activateConversation(state, action: PayloadAction<{ id: string }>) {
      const newConversation = action.payload.id;
      state.activeConversationId = newConversation;
      const conversation = getConversation(state, newConversation);
      conversation.unreadMessages = 0;
      conversation.isTypingState = {};
    },
    addConversationChatMessage(state, action: PayloadAction<SalesfloorChannelUpdate>) {
      const { message } = action.payload;
      appendConversationMessage(state, message);
    },
    removeConversationChatMessage(state, action: PayloadAction<SalesfloorChannelUpdate>) {
      const { message } = action.payload;
      removeConversationMessage(state, message);
    },
    receivedConversationChatMessage(
      state,
      action: PayloadAction<SalesfloorChannelUpdate>,
    ) {
      const { message, ownUser } = action.payload;
      switch (message.type) {
        case SalesfloorChatMessageType.CONTENT:
          setTypingState(state, {
            conversationId: message.conversationId,
            participant: message.sender,
            isTyping: false,
          });
          appendConversationMessage(state, message);
          break;
        case SalesfloorChatMessageType.TYPING:
          updateIsTypingState(state, ownUser, message);
          break;
        default:
          log.error('receivedConversationChatMessage unknown message type', message);
          break;
      }
    },
    addConversationParticipants(
      state,
      action: PayloadAction<SalesfloorRoomChatParticipant[]>,
    ) {
      state.chatParticipants = [...state.chatParticipants, ...action.payload];
    },
    removeConversationParticipant(state, action: PayloadAction<string>) {
      state.chatParticipants = state.chatParticipants.filter(
        chatParticipant => chatParticipant.id !== action.payload,
      );
    },
    updateConversationParticipant(
      state,
      action: PayloadAction<SalesfloorRoomChatParticipant>,
    ) {
      state.chatParticipants = state.chatParticipants.map(
        (chatParticipant: SalesfloorRoomChatParticipant) => {
          if (chatParticipant.id === action.payload.id) {
            return action.payload;
          } else {
            return chatParticipant;
          }
        },
      );
    },
    markConversationParticipantGone(state, action: PayloadAction<string>) {
      state.chatParticipants = state.chatParticipants.map(
        (chatParticipant: SalesfloorRoomChatParticipant) => {
          if (chatParticipant.id === action.payload) {
            return {
              ...chatParticipant,
              status: 'gone' as SalesfloorRoomChatParticipantStatus,
            };
          } else {
            return chatParticipant;
          }
        },
      );
    },
    setParticipantTyping(
      state,
      action: PayloadAction<{
        conversationId: string;
        participant: SalesfloorParticipant;
        isTyping: boolean;
      }>,
    ) {
      log.debug('setParticipantTyping', action.payload);
      setTypingState(state, action.payload);
    },
  },
});

const selectPrivateRooms = (state: AppState) => state.salesfloor.privateRooms;
const selectTeamRooms = (state: AppState) => state.salesfloor.teamRooms;
const selectPublicRooms = (state: AppState) => state.salesfloor.publicRooms;
const selectLiveListenRooms = (state: AppState) => state.salesfloor.liveListenRooms;

export const selectAllRooms = createSelector(
  [selectPrivateRooms, selectTeamRooms, selectPublicRooms],
  (privateRooms, teamRooms, publicRooms) => {
    return [...privateRooms, ...teamRooms, ...publicRooms];
  },
);

export const selectAllRoomsWithLiveListen = createSelector(
  [selectPrivateRooms, selectTeamRooms, selectPublicRooms, selectLiveListenRooms],
  (privateRooms, teamRooms, publicRooms, liveListenRooms) => {
    return [...privateRooms, ...teamRooms, ...publicRooms, ...liveListenRooms];
  },
);

export const createMessagesSelector = (conversationId: string) => (state: AppState) => {
  const conversation = state.salesfloor.conversations[conversationId];
  if (!conversation) return [];

  return conversation.chatMessages;
};

export const selectActiveConversationId = (state: AppState) =>
  state.salesfloor.activeConversationId;

export const selectConversations = (state: AppState) => state.salesfloor.conversations;

export const selectAllParticipants = createSelector([selectAllRooms], allRooms => {
  return allRooms
    ? allRooms.reduce((acc: SalesfloorRoomParticipant[], room: SalesfloorRoom) => {
        acc.push(...room.participants);
        return acc;
      }, [] as SalesfloorRoomParticipant[])
    : [];
});

export const selectChatParticipants = (state: AppState) =>
  state.salesfloor.chatParticipants;

export const selectActiveConversationMessages = createSelector(
  [selectActiveConversationId, selectConversations],
  (activeConversationId, conversations) => {
    return conversations[activeConversationId]?.chatMessages || [];
  },
);

export interface RoomConversationSummary {
  id: string;
  icon: string;
  name: string;
  current: boolean;
  notificationCount: number;
}

export interface ParticipantConversationSummary {
  id: string;
  avatarUrl: string | null;
  name: string;
  current: boolean;
  notificationCount: number;
  user: SalesfloorRoomChatParticipant;
}

export type ConversationSummary =
  | RoomConversationSummary
  | ParticipantConversationSummary;

export const selectConversationSummaries = createSelector(
  [selectActiveConversationId, selectAllRooms, selectConversations],
  (activeConversationId, rooms, conversations): ConversationSummary[] => {
    return rooms.map((r: SalesfloorRoom) => {
      const conversationId = `room:${r.id}`;
      const conversation = conversations[conversationId];
      return {
        id: conversationId,
        icon: r.emoji,
        name: r.name,
        current: conversationId == activeConversationId,
        notificationCount: conversation?.unreadMessages ?? 0,
      };
    });
  },
);

export const getPrivateConversationId = (
  participantAId: string,
  participantBId: string,
) => {
  if (participantAId > participantBId) {
    return `${participantBId}:${participantAId}`;
  } else {
    return `${participantAId}:${participantBId}`;
  }
};

export const selectParticipantSummaries = createSelector(
  [
    selectActiveConversationId,
    selectChatParticipants,
    selectConversations,
    selectActiveUser,
    selectActivePublicUser,
  ],
  (
    activeConversationId,
    chatParticipants,
    conversations,
    activeUser,
    activePublicUser,
  ): ParticipantConversationSummary[] => {
    return (
      chatParticipants
        // TODO: consider factoring out the participant ID generation into a single function
        .filter(p => {
          return (
            p.id !==
            (activeUser
              ? `user_${activeUser?.id}`
              : `public_user_${activePublicUser?.id}`)
          );
        })
        .map((p: SalesfloorRoomChatParticipant) => {
          const conversationId = `dm:${getPrivateConversationId(
            activeUser ? `user_${activeUser?.id}` : `public_user_${activePublicUser?.id}`,
            p.id,
          )}`;
          const conversation = conversations[conversationId];
          return {
            id: conversationId,
            name: p.displayName,
            avatarUrl: p.avatarUrl,
            user: p,
            current: conversationId == activeConversationId,
            notificationCount: conversation?.unreadMessages ?? 0,
          };
        })
    );
  },
);

export const selectActiveConversationSummary = createSelector(
  [selectActiveConversationId, selectConversationSummaries, selectParticipantSummaries],
  (activeConversationId, conversationSummaries, participantSummaries) => {
    if (activeConversationId.startsWith('room:')) {
      const activeRoom = conversationSummaries.find(r => r.id == activeConversationId);
      if (activeRoom) {
        return activeRoom;
      }
    } else {
      const activePrivateConversation = participantSummaries.find(
        p => p.id == activeConversationId,
      );
      if (activePrivateConversation) {
        return activePrivateConversation;
      }
    }

    return conversationSummaries[0];
  },
);

export const selectTotalUnreadMessages = createSelector(
  [selectConversations],
  conversations => {
    let count = 0;
    for (const [_, conversation] of Object.entries(conversations)) {
      count += conversation.unreadMessages;
    }
    return count;
  },
);

export const {
  setChatOpen,
  setMode,
  setScreenSharing,
  setScreenShareInFocus,
  unsetScreenShareInFocus,
  setSettingsModalVisible,
  setReaction,
  setSidebardCollapsed,
  setSalesfloorRooms,
  updateSalesfloorRoom,
  removeSalesfloorRoom,
  setLocalParticipantStatus,
  setListenedDialingParticipantIds,
  activateConversation,
  receivedConversationChatMessage,
  setParticipantTyping,
  addConversationParticipants,
  removeConversationParticipant,
  markConversationParticipantGone,
  updateConversationParticipant,
} = salesfloorSlice.actions;

export default salesfloorSlice.reducer;
